/**
 * F42 — admin venue MERGE + BULK API client (B2 surface). WAVE-2 (B2).
 *
 * Real adminFetch calls. Component tests drive `adminVenuesMerge.fixture.ts`
 * (swapped via the vitest alias). Mirrors the F23/F39 real+fixture split.
 *
 *   GET  /admin/venues/merge/preview?sourceVenueId=&canonicalVenueId=  → MergeImpactPreview
 *   POST /admin/venues/merge                                           → { correlationId }
 *   POST /admin/venues/bulk   (Idempotency-Key header)                 → BulkActionResult
 *
 * Permission gating is server-side; <ProtectedRoute> gates navigation.
 */
import type {
  BulkActionResult,
  BulkVenueActionInput,
  MergeImpactPreview,
  MergeVenuesInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient.js';

// 🔴 Every endpoint below wraps its payload in the shared `{ data }` admin
// envelope (venue-merge.ts:58/84 + bulk-venues.ts:85) — these wrappers unwrap
// it so callers get the bare contract shape. The fixture mirrors the UNWRAPPED
// (post-unwrap) contract, since the vitest alias replaces this whole module —
// not the HTTP layer. (V-03/V-04/V-23 fix: the old client returned the raw
// envelope, so the merge preview rendered empty + the conflict gate never
// engaged + BulkPanel would crash on `result.results`.)

export const adminVenuesMergeApi = {
  /** GET /admin/venues/merge/preview — per-table re-parent counts + conflicts. */
  previewMerge: async (
    sourceVenueId: string,
    canonicalVenueId: string,
  ): Promise<MergeImpactPreview> => {
    const res = await adminFetch<{ readonly data: MergeImpactPreview }>(
      `/admin/venues/merge/preview${buildQuery({ sourceVenueId, canonicalVenueId })}`,
    );
    return res.data;
  },

  /** POST /admin/venues/merge — super_admin only; idempotent; 409 ALREADY_MERGED. */
  merge: async (input: MergeVenuesInput): Promise<{ correlationId: string }> => {
    const res = await adminFetch<{ readonly data: { correlationId: string } }>(
      '/admin/venues/merge',
      {
        method: 'POST',
        body: input as unknown as Record<string, unknown>,
      },
    );
    return res.data;
  },

  /** POST /admin/venues/bulk — Idempotency-Key header; partial-success result. */
  bulk: async (input: BulkVenueActionInput, idempotencyKey: string): Promise<BulkActionResult> => {
    const res = await adminFetch<{ readonly data: BulkActionResult }>('/admin/venues/bulk', {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: input as unknown as Record<string, unknown>,
    });
    return res.data;
  },
};
